import React from 'react';

/**
 * Pioneer Availability Badge — replaces the public-facing "X / 250 claimed"
 * counter with a privacy-respecting traffic-light indicator.
 *
 * Tiers (based on `claimed` count out of 250 seats):
 *   - GREEN   (0  – 100 claimed)  → "Qualifying: Open"
 *   - YELLOW  (101 – 200 claimed) → "Qualifying: Limited"
 *   - RED     (201 – 250 claimed) → "Qualifying: Final Round"
 *   - DONE    (>= 250 claimed)    → "Qualifying: Closed"
 *
 * The numeric `claimed` value is never rendered — only the tier color and
 * label appear in the DOM.
 */
const tierFor = (claimed) => {
  const n = Number(claimed) || 0;
  if (n >= 250) {
    return {
      key: 'closed',
      label: 'Qualifying: Closed',
      dot: 'bg-slate-500',
      ring: 'ring-slate-500/40',
      pillBg: 'bg-slate-500/10',
      pillBorder: 'border-slate-500/40',
      pillText: 'text-slate-300',
      glow: '0 0 18px rgba(100,116,139,0.35)',
    };
  }
  if (n >= 201) {
    return {
      key: 'red',
      label: 'Qualifying: Final Round',
      dot: 'bg-red-500',
      ring: 'ring-red-500/40',
      pillBg: 'bg-red-500/10',
      pillBorder: 'border-red-500/40',
      pillText: 'text-red-300',
      glow: '0 0 18px rgba(239,68,68,0.45)',
    };
  }
  if (n >= 101) {
    return {
      key: 'yellow',
      label: 'Qualifying: Limited',
      dot: 'bg-amber-400',
      ring: 'ring-amber-400/40',
      pillBg: 'bg-amber-400/10',
      pillBorder: 'border-amber-400/40',
      pillText: 'text-amber-200',
      glow: '0 0 18px rgba(234,179,8,0.45)',
    };
  }
  return {
    key: 'green',
    label: 'Qualifying: Open',
    dot: 'bg-emerald-400',
    ring: 'ring-emerald-400/40',
    pillBg: 'bg-emerald-400/10',
    pillBorder: 'border-emerald-400/40',
    pillText: 'text-emerald-300',
    glow: '0 0 18px rgba(52,211,153,0.45)',
  };
};

/**
 * Compact pill variant — for inline / sidebar contexts.
 */
export const PioneerAvailabilityPill = ({ claimed }) => {
  const t = tierFor(claimed);
  return (
    <span
      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${t.pillBg} ${t.pillBorder} ${t.pillText} text-[11px] uppercase tracking-[0.25em] font-semibold`}
      data-testid="pioneer-availability-pill"
      data-tier={t.key}
    >
      <span
        className={`w-2 h-2 rounded-full ${t.dot}`}
        style={{ boxShadow: t.glow }}
      />
      {t.label}
    </span>
  );
};

/**
 * Hero variant — for prominent placement on /pioneers and the landing teaser.
 * Replaces the old "X of 250 seats claimed" widget entirely. Renders no
 * numeric counts whatsoever.
 */
export const PioneerAvailabilityBadge = ({ claimed, compact = false }) => {
  const t = tierFor(claimed);
  return (
    <div
      className={`inline-flex items-center gap-3 rounded-full border ${t.pillBg} ${t.pillBorder} ${
        compact ? 'px-4 py-2' : 'px-5 py-3'
      }`}
      data-testid="pioneer-availability-badge"
      data-tier={t.key}
    >
      <span
        className={`relative flex w-3 h-3 ${compact ? '' : 'w-3.5 h-3.5'}`}
      >
        <span
          className={`motion-safe:animate-ping absolute inline-flex w-full h-full rounded-full opacity-60 ${t.dot}`}
        />
        <span
          className={`relative inline-flex rounded-full w-full h-full ${t.dot}`}
          style={{ boxShadow: t.glow }}
        />
      </span>
      <span className={`${t.pillText} ${compact ? 'text-[11px]' : 'text-xs'} uppercase tracking-[0.3em] font-semibold`}>
        {t.label}
      </span>
    </div>
  );
};

export default PioneerAvailabilityBadge;
